import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation, Link, Navigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import api from "../lib/api";
import { fmtEur } from "../lib/utils";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import {
  CheckCircle, ArrowRight, ArrowLeft, Upload, Lock, ShieldCheck,
  XCircle, Sparkle, Storefront, User, Buildings, Crown, RocketLaunch, Lightning, House,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getLocalizedCountries } from "../lib/countries";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

let _stripe = null;
const getStripe = () =>
  (_stripe ||= api.get("/stripe/config").then(({ data }) => loadStripe(data.publishable_key)));

const TOTAL_STEPS = 6;

const STORE_LOGOS = {
  shopify:     "/store-logos/shopify.webp",
  woocommerce: "/store-logos/woocommerce.png",
  etsy:        "/store-logos/etsy.png",
};

export default function Signup() {
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useI18n();
  const [token, setToken] = useState(() => localStorage.getItem("tf_signup_token"));
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    account: { first_name: "", last_name: "", phone: "", email: "", password: "" },
    company: {
      entity_type: "business",
      company_name: "",
      vat: "",
      codice_fiscale: "",
      sdi_or_pec: "",
      address_line1: "",
      city: "",
      postal_code: "",
      province: "",
      country: "IT",
    },
    store: { platform: "shopify", url: "", logo_url: null },
    plan: { plan: "pro", period: "monthly" },
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      api.post("/signup/start").then(({ data }) => {
        localStorage.setItem("tf_signup_token", data.token);
        setToken(data.token);
      });
    } else {
      api.get(`/signup/${token}`)
        .then(({ data }) => {
          const bs = Math.max(1, Math.min(5, data.step || 1));
          const ui = bs >= 4 ? bs + 1 : bs;
          setStep(ui);
        })
        .catch(() => { localStorage.removeItem("tf_signup_token"); window.location.reload(); });
    }
  }, [token]);

  const upd = (section, patch) => setData((p) => ({ ...p, [section]: { ...p[section], ...patch } }));

  const goNext = async (overrides) => {
    if (!token) return;
    setBusy(true);
    try {
      if (step === 1) await api.post(`/signup/${token}/step/1`, data.account);
      else if (step === 2) await api.post(`/signup/${token}/step/2`, data.company);
      else if (step === 3) await api.post(`/signup/${token}/step/3`, { platform: data.store.platform, url: data.store.url });
      else if (step === 5) await api.post(`/signup/${token}/step/4`, { ...data.plan, ...overrides });
      setStep(step + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error"));
    } finally { setBusy(false); }
  };

  const goBack = () => step > 1 && setStep(step - 1);

  // Task 3: se l'utente ha già completato l'onboarding (è autenticato), redirigilo alla dashboard
  if (!authLoading && user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/client"} replace />;
  }

  if (!token) {
    return <div className="min-h-screen flex items-center justify-center text-[#5A6E63]" style={{ background: "#F5F2E8" }}>{t("su_loading")}</div>;
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#F5F2E8" }}>
      <TopBar step={step} />
      <main className="flex-1 flex items-start justify-center px-6 py-10 md:py-14">
        <div className={`w-full ${step === 5 ? "max-w-4xl" : "max-w-2xl"} transition-all`}>
          <div className="bg-white border border-[#E0DFD4] rounded-[24px] p-8 md:p-14">
            {step === 1 && <StepAccount data={data.account} set={(p) => upd("account", p)} onNext={goNext} busy={busy} />}
            {step === 2 && <StepBilling data={data.company} set={(p) => upd("company", p)} onNext={goNext} onBack={goBack} busy={busy} />}
            {step === 3 && <StepStore   data={data.store}   set={(p) => upd("store", p)}   onNext={goNext} onBack={goBack} busy={busy} />}
            {step === 4 && <StepBrand   data={data.store}   set={(p) => upd("store", p)}   onNext={goNext} onBack={goBack} busy={busy} token={token} />}
            {step === 5 && <StepPlan    data={data.plan}    set={(p) => upd("plan", p)}    onNext={goNext} onBack={goBack} busy={busy} />}
            {step === 6 && <StepCheckout data={data} token={token} onBack={goBack} onSuccess={(res) => {
              localStorage.removeItem("tf_signup_token");
              const ref = res?.signup_ref;
              nav(ref ? `/signup/success?ref=${ref}` : "/signup/success", { state: res });
            }} />}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function TopBar({ step }) {
  const { t } = useI18n();
  return (
    <header className="sticky top-0 z-50 backdrop-blur-sm" style={{ background: "#F5F2E8" }}>
      <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link
          to="/"
          className="font-display font-black tracking-tight text-[#193529] hover:opacity-80 transition-opacity text-base md:text-lg"
          data-testid="wizard-logo-link"
          aria-label={t("su_back_home")}
        >
          Teefarm Connect
        </Link>
        <div className="text-xs tabular-nums text-[#5A6E63] flex items-center gap-3">
          <LanguageSwitcher />
          <span>
            <span className="font-bold text-[#193529] tf-numeric">{String(step).padStart(2, "0")}</span>
            <span className="mx-1">/</span>
            <span className="tf-numeric">{String(TOTAL_STEPS).padStart(2, "0")}</span>
          </span>
        </div>
      </div>
      <ProgressBar step={step} />
    </header>
  );
}

function ProgressBar({ step }) {
  const { t } = useI18n();
  const STEP_TITLES = [t("su_step_account"), t("su_step_billing"), t("su_step_store"), t("su_step_brand"), t("su_step_plan"), t("su_step_payment")];
  const currentIndex = step - 1;
  // Fill percentage — animate to previous checkpoint position, so lime "flows" toward the active dot
  const fillPct = currentIndex === 0 ? 0 : (currentIndex / (TOTAL_STEPS - 1)) * 100;
  return (
    <div className="max-w-3xl mx-auto px-6 pt-2 pb-6" data-testid="progress-bar">
      <div className="relative h-10">
        {/* Rail (background line) */}
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] bg-[#E0DFD4]" />
        {/* Filled line — animates smoothly */}
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] bg-[#CCF22C] transition-all duration-500 ease-in-out"
          style={{ width: `${fillPct}%` }}
        />
        {/* Checkpoints */}
        <div className="absolute inset-0 flex items-center justify-between">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => {
            const done = i < currentIndex;
            const active = i === currentIndex;
            return (
              <div key={i} className="relative flex flex-col items-center">
                <div
                  data-testid={`checkpoint-${i + 1}`}
                  data-state={done ? "done" : active ? "active" : "future"}
                  className={`rounded-full transition-all duration-500 ease-in-out ${
                    active
                      ? "w-4 h-4 bg-[#CCF22C] ring-4 ring-[#CCF22C]/30 animate-checkpoint-pop"
                      : done
                      ? "w-3 h-3 bg-[#CCF22C]"
                      : "w-3 h-3 bg-[#E0DFD4]"
                  }`}
                />
                {/* Step title above the active checkpoint (desktop only) */}
                <span
                  className={`absolute -top-6 text-[10px] uppercase tracking-widest whitespace-nowrap transition-opacity duration-300 hidden md:block ${
                    active ? "opacity-100 text-[#193529] font-bold" : "opacity-0"
                  }`}
                >
                  {STEP_TITLES[i]}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const Footer = () => (
  <footer className="py-6 text-center text-[10px] tracking-widest uppercase text-[#5A6E63]/60">
    teefarm connect · onboarding
  </footer>
);

function ScreenHeader({ eyebrow, title, subtitle }) {
  const { t } = useI18n();
  return (
    <div className="mb-10">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#5A6E63] mb-4 tf-numeric">
        {t("su_step_of", { n: eyebrow, total: String(TOTAL_STEPS).padStart(2, "0") })}
      </div>
      <h1 className="font-display font-black text-3xl md:text-4xl leading-[1.05] tracking-tight text-[#193529]">
        {title}
      </h1>
      {subtitle && <p className="text-sm text-[#5A6E63] mt-3 max-w-md">{subtitle}</p>}
    </div>
  );
}

function FocusedInput({ label, hint, error, ...props }) {
  return (
    <div>
      {label && <label className="block text-[11px] uppercase tracking-widest text-[#5A6E63] mb-2">{label}</label>}
      <input
        {...props}
        className="w-full bg-transparent border-0 border-b-2 border-[#193529]/20 focus:border-[#193529] outline-none text-base md:text-lg py-3.5 text-[#193529] placeholder:text-[#5A6E63]/40 transition-colors"
      />
      {(hint || error) && (
        <div className={`text-xs mt-2 ${error ? "text-[#C4452E]" : "text-[#5A6E63]"}`}>{error || hint}</div>
      )}
    </div>
  );
}

function ActionBar({ onBack, onNext, disabled, busy, cta, testId = "next-btn" }) {
  const { t } = useI18n();
  return (
    <div className="flex items-center justify-between mt-12">
      {onBack ? (
        <button type="button" onClick={onBack} className="text-sm text-[#5A6E63] hover:text-[#193529] inline-flex items-center gap-1.5" data-testid="back-btn">
          <ArrowLeft size={14} weight="bold" /> {t("su_back")}
        </button>
      ) : <div />}
      <button
        type="button"
        onClick={() => onNext()}
        disabled={disabled || busy}
        className="tf-pill-primary"
        data-testid={testId}
      >
        {busy ? "…" : (cta || t("su_continue"))} <ArrowRight size={14} weight="bold" />
      </button>
    </div>
  );
}

/* ---------------- Step 1 · Anagrafica ---------------- */
function StepAccount({ data, set, onNext, busy }) {
  const { t } = useI18n();
  const emailOk = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(data.email || "");
  const valid = data.first_name?.trim().length >= 2
    && data.last_name?.trim().length >= 2
    && data.phone?.trim().length >= 6
    && emailOk
    && data.password?.length >= 8;
  return (
    <section>
      <ScreenHeader eyebrow="01" title={t("su_s1_title")} subtitle={t("su_s1_subtitle")} />
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <FocusedInput label={t("su_first_name")} value={data.first_name} onChange={(e) => set({ first_name: e.target.value })} placeholder={t("su_name_placeholder")} data-testid="acc-first-name" autoFocus />
          <FocusedInput label={t("su_last_name")} value={data.last_name} onChange={(e) => set({ last_name: e.target.value })} placeholder={t("su_lastname_placeholder")} data-testid="acc-last-name" />
        </div>
        <FocusedInput label={t("su_phone")} value={data.phone} onChange={(e) => set({ phone: e.target.value })} placeholder={t("su_phone_placeholder")} data-testid="acc-phone" />
        <FocusedInput label={t("su_email")} type="email" value={data.email} onChange={(e) => set({ email: e.target.value.toLowerCase() })} placeholder={t("su_email_placeholder")} data-testid="acc-email" />
        <FocusedInput label={t("su_password")} type="password" value={data.password} onChange={(e) => set({ password: e.target.value })} placeholder={t("su_password_placeholder")} hint={t("su_password_hint")} data-testid="acc-password" />
      </div>
      <ActionBar onNext={onNext} disabled={!valid} busy={busy} />
    </section>
  );
}

/* ---------------- Step 2 · Profilo Fatturazione ---------------- */
function StepBilling({ data, set, onNext, onBack, busy }) {
  const { t, lang } = useI18n();
  const COUNTRIES = getLocalizedCountries(lang);
  const isBusiness = data.entity_type === "business";
  const cfClean = (data.codice_fiscale || "").toUpperCase().replace(/\s/g, "");
  const cfOk = /^[A-Z0-9]{16}$/.test(cfClean);
  const vatClean = (data.vat || "").toUpperCase().replace(/\s/g, "");
  const vatOk = /^(IT)?\d{11}$/.test(vatClean) || /^[A-Z0-9]{11,16}$/.test(vatClean);
  const sdiOk = data.sdi_or_pec && (/^[A-Z0-9]{7}$/i.test(data.sdi_or_pec) || /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(data.sdi_or_pec));
  const addrOk = data.address_line1 && data.city && data.postal_code && data.province && data.country;
  const valid = isBusiness
    ? (data.company_name && vatOk && sdiOk && addrOk)
    : (cfOk && addrOk);

  return (
    <section>
      <ScreenHeader eyebrow="02" title={t("su_s2_title")} subtitle={t("su_s2_subtitle")} />

      <div className="grid grid-cols-2 gap-3 mb-8">
        <SegmentButton
          active={!isBusiness}
          onClick={() => set({ entity_type: "private" })}
          icon={User}
          title={t("su_entity_private")}
          subtitle={t("su_entity_private_sub")}
          testId="entity-private"
        />
        <SegmentButton
          active={isBusiness}
          onClick={() => set({ entity_type: "business" })}
          icon={Buildings}
          title={t("su_entity_business")}
          subtitle={t("su_entity_business_sub")}
          testId="entity-business"
        />
      </div>

      <div className="space-y-6">
        {isBusiness ? (
          <>
            <FocusedInput label={t("su_company_name")} value={data.company_name} onChange={(e) => set({ company_name: e.target.value })} placeholder={t("su_company_placeholder")} data-testid="comp-name" />
            <div className="grid md:grid-cols-2 gap-6">
              <FocusedInput label={t("su_vat")} value={data.vat} onChange={(e) => set({ vat: e.target.value.toUpperCase() })} placeholder={t("su_vat_placeholder")} error={data.vat && !vatOk ? t("su_vat_invalid") : null} data-testid="comp-vat" />
              <FocusedInput label={t("su_sdi")} value={data.sdi_or_pec} onChange={(e) => set({ sdi_or_pec: e.target.value })} placeholder={t("su_sdi_placeholder")} error={data.sdi_or_pec && !sdiOk ? t("su_sdi_invalid") : null} data-testid="comp-sdi" />
            </div>
          </>
        ) : (
          <FocusedInput
            label={t("su_cf")}
            value={data.codice_fiscale}
            onChange={(e) => set({ codice_fiscale: e.target.value.toUpperCase() })}
            placeholder={t("su_cf_placeholder")}
            error={data.codice_fiscale && !cfOk ? t("su_cf_invalid") : null}
            data-testid="comp-cf"
          />
        )}

        <FocusedInput label={t("su_address")} value={data.address_line1} onChange={(e) => set({ address_line1: e.target.value })} placeholder={t("su_addr_placeholder")} data-testid="comp-addr1" />
        <div className="grid grid-cols-3 gap-4">
          <FocusedInput label={t("su_cap")} value={data.postal_code} onChange={(e) => set({ postal_code: e.target.value })} placeholder={t("su_cap_placeholder")} data-testid="comp-cap" />
          <FocusedInput label={t("su_city")} value={data.city} onChange={(e) => set({ city: e.target.value })} placeholder={t("su_city_placeholder")} data-testid="comp-city" />
          <FocusedInput label={t("su_province")} value={data.province} onChange={(e) => set({ province: e.target.value.toUpperCase().slice(0, 2) })} placeholder={t("su_province_placeholder")} data-testid="comp-province" />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-[#5A6E63] mb-3">{t("su_country")}</label>
          <select
            value={data.country}
            onChange={(e) => set({ country: e.target.value })}
            data-testid="comp-country"
            className="w-full bg-transparent border-0 border-b-2 border-[#193529]/20 focus:border-[#193529] outline-none text-base md:text-lg py-3.5 text-[#193529] transition-colors appearance-none cursor-pointer"
            style={{ backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23193529' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 0 center", backgroundSize: "18px" }}
          >
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>
      <ActionBar onBack={onBack} onNext={onNext} disabled={!valid} busy={busy} />
    </section>
  );
}

function SegmentButton({ active, onClick, icon: Icon, title, subtitle, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left px-5 py-4 rounded-2xl border-2 transition-all flex items-center gap-3 ${
        active
          ? "border-[#193529] bg-[#193529] text-white"
          : "border-[#193529]/15 text-[#193529] hover:border-[#193529]/50"
      }`}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${active ? "bg-[#CCF22C] text-[#193529]" : "bg-[#193529]/5 text-[#193529]"}`}>
        <Icon size={20} weight="fill" />
      </div>
      <div>
        <div className="font-display font-black text-base">{title}</div>
        <div className={`text-[10px] uppercase tracking-widest ${active ? "text-white/70" : "text-[#5A6E63]"}`}>{subtitle}</div>
      </div>
    </button>
  );
}

/* ---------------- Step 3 · Store platform ---------------- */
const PLATFORMS = [
  { key: "shopify",     name: "Shopify",     enabled: true  },
  { key: "woocommerce", name: "WooCommerce", enabled: false },
  { key: "etsy",        name: "Etsy",        enabled: false },
];
function StepStore({ data, set, onNext, onBack, busy }) {
  const { t } = useI18n();
  const valid = data.platform === "shopify"; // URL now optional — collected later on Teefarm Connect
  return (
    <section>
      <ScreenHeader eyebrow="03" title={t("su_s3_title")} subtitle={t("su_s3_subtitle")} />
      <div className="space-y-8">
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-[#5A6E63] mb-3">{t("su_platform_label")}</label>
          <div className="grid grid-cols-3 gap-3">
            {PLATFORMS.map((p) => {
              const active = data.platform === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => p.enabled && set({ platform: p.key })}
                  disabled={!p.enabled}
                  data-testid={`platform-${p.key}`}
                  className={`relative py-6 px-3 rounded-2xl border-2 transition-all flex flex-col items-center justify-center gap-2 min-h-[120px] ${
                    !p.enabled
                      ? "border-[#193529]/10 bg-[#F5F2E8]/40 opacity-50 cursor-not-allowed grayscale"
                      : active
                      ? "border-[#193529] bg-[#193529]/5"
                      : "border-[#193529]/20 hover:border-[#193529]/50"
                  }`}
                >
                  {!p.enabled && (
                    <span className="absolute top-2 right-2 text-[9px] uppercase tracking-widest bg-[#D4A017]/20 text-[#8a6a00] px-2 py-0.5 rounded-full font-bold">
                      {t("su_coming_soon")}
                    </span>
                  )}
                  <div className="flex items-center justify-center h-10">
                    <img src={STORE_LOGOS[p.key]} alt={p.name} className="max-h-10 max-w-[100px] object-contain" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-[16px] bg-[#F5F2E8]/70 border border-[#193529]/10 p-4 flex gap-3 items-start">
          <div className="w-8 h-8 rounded-lg bg-[#CCF22C] flex items-center justify-center shrink-0">
            <Sparkle size={16} weight="fill" className="text-[#193529]" />
          </div>
          <p className="text-xs text-[#193529] leading-relaxed">
            {t("su_s3_note")}
          </p>
        </div>
      </div>
      <ActionBar onBack={onBack} onNext={onNext} disabled={!valid} busy={busy} />
    </section>
  );
}

/* ---------------- Step 4 · Brand logo ---------------- */
function StepBrand({ data, set, onNext, onBack, busy, token }) {
  const { t } = useI18n();
  const [uploading, setUploading] = useState(false);
  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data: res } = await api.post(`/signup/${token}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set({ logo_url: res.logo_url });
      toast.success(t("su_logo_upload_success"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("su_logo_upload_fail")); }
    finally { setUploading(false); }
  };
  return (
    <section>
      <ScreenHeader
        eyebrow="04"
        title={t("su_s4_title")}
        subtitle={t("su_s4_subtitle")}
      />
      <div className="space-y-6">
        <label className="block cursor-pointer">
          <div className={`border-2 border-dashed rounded-2xl p-10 md:p-14 text-center transition-all ${
            data.logo_url ? "border-[#193529] bg-white" : "border-[#193529]/25 hover:border-[#193529]/60 bg-[#F5F2E8]/40"
          }`}>
            {data.logo_url ? (
              <>
                <img src={data.logo_url} alt="logo" className="max-h-32 mx-auto object-contain" data-testid="logo-preview" />
                <div className="mt-4 text-xs text-[#5A6E63] flex items-center justify-center gap-2">
                  <CheckCircle size={14} weight="fill" className="text-[#4A8A3E]" /> {t("su_logo_uploaded")}
                </div>
              </>
            ) : (
              <>
                <div className="w-14 h-14 mx-auto rounded-full bg-[#193529]/5 flex items-center justify-center">
                  <Upload size={22} weight="bold" className="text-[#193529]" />
                </div>
                <div className="mt-4 font-display font-bold text-lg text-[#193529]">
                  {uploading ? t("su_logo_uploading") : t("su_logo_upload_cta")}
                </div>
                <div className="text-xs text-[#5A6E63] mt-1">{t("su_logo_format")}</div>
              </>
            )}
          </div>
          <input
            type="file"
            accept=".png,.svg,.jpg,.jpeg,.webp"
            className="hidden"
            onChange={(e) => uploadLogo(e.target.files?.[0])}
            data-testid="logo-input"
          />
        </label>

        {/* Format hint card */}
        <div className="rounded-[16px] bg-[#F5F2E8]/70 border border-[#193529]/10 p-4 flex gap-3 items-start">
          <div className="w-8 h-8 rounded-lg bg-[#CCF22C] flex items-center justify-center shrink-0">
            <Sparkle size={16} weight="fill" className="text-[#193529]" />
          </div>
          <p className="text-xs text-[#193529] leading-relaxed">
            {t("su_logo_format_hint")}
          </p>
        </div>

        <div className="text-center">
          <button type="button" onClick={() => onNext()} className="text-xs text-[#5A6E63] hover:text-[#193529] underline underline-offset-4" data-testid="skip-logo">
            {t("su_skip_logo")}
          </button>
        </div>
      </div>
      <ActionBar onBack={onBack} onNext={onNext} busy={busy} />
    </section>
  );
}

/* ---------------- Step 5 · Plan ---------------- */
function usePlansUi() {
  const { t } = useI18n();
  return [
    {
      key: "start", name: "Start",
      tagline: t("su_plan_start_tagline"),
      priceMonthly: 19.99, priceYearlyPerMonth: 15.99,
      icon: RocketLaunch, iconColor: "#4A8A3E",
      cta: t("su_plan_start_cta"),
      features: [
        { text: t("su_feat_start_1") },
        { text: t("su_feat_start_2") },
        { text: t("su_feat_start_3") },
        { text: t("su_feat_start_4") },
        { text: t("su_feat_start_5") },
      ],
    },
    {
      key: "pro", name: "Pro",
      tagline: t("su_plan_pro_tagline"),
      priceMonthly: 29.99, priceYearlyPerMonth: 23.99,
      icon: Lightning, iconColor: "#193529",
      cta: t("su_plan_pro_cta"),
      recommended: true,
      features: [
        { text: t("su_feat_pro_0"), pre: true },
        { text: t("su_feat_pro_1") },
        { text: t("su_feat_pro_2") },
        { text: t("su_feat_pro_3") },
        { text: t("su_feat_pro_4"), highlight: true },
        { text: t("su_feat_pro_5") },
      ],
    },
    {
      key: "elite", name: "Elite",
      tagline: t("su_plan_elite_tagline"),
      priceMonthly: 49.99, priceYearlyPerMonth: 39.99,
      icon: Crown, iconColor: "#D4A017",
      cta: t("su_plan_elite_cta"),
      features: [
        { text: t("su_feat_elite_0"), pre: true },
        { text: t("su_feat_elite_1"), highlight: true, bold: true },
        { text: t("su_feat_elite_2"), highlight: true },
        { text: t("su_feat_elite_3") },
        { text: t("su_feat_elite_4") },
        { text: t("su_feat_elite_5") },
      ],
    },
  ];
}

function StepPlan({ data, set, onNext, onBack, busy }) {
  const { t } = useI18n();
  const PLANS_UI = usePlansUi();
  const isYearly = data.period === "yearly";
  const displayPrice = (p) => isYearly ? p.priceYearlyPerMonth : p.priceMonthly;

  const selectPlanAndContinue = (planKey) => {
    set({ plan: planKey });
    // Pass the just-picked plan explicitly so the API call doesn't read a stale `data.plan`
    // from the pre-click render closure (React state updates are async).
    onNext({ plan: planKey });
  };

  return (
    <section>
      <ScreenHeader eyebrow="05" title={t("su_s5_title")} subtitle={t("su_s5_subtitle")} />

      {/* Billing toggle — with prominent savings when yearly */}
      <div className="flex justify-center mb-10">
        <div className="inline-flex items-center gap-3">
          <div className="inline-flex p-1 rounded-full bg-[#193529]/5 border border-[#193529]/10">
            <button
              type="button"
              onClick={() => set({ period: "monthly" })}
              data-testid="period-monthly"
              className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${!isYearly ? "bg-[#193529] text-white" : "text-[#5A6E63]"}`}
            >{t("su_monthly")}</button>
            <button
              type="button"
              onClick={() => set({ period: "yearly" })}
              data-testid="period-yearly"
              className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${isYearly ? "bg-[#193529] text-white" : "text-[#5A6E63]"}`}
            >{t("su_yearly")}</button>
          </div>
          <span className={`text-[11px] uppercase tracking-widest font-bold px-3 py-1 rounded-full transition-all ${
            isYearly ? "bg-[#CCF22C] text-[#193529]" : "bg-[#193529]/5 text-[#5A6E63]"
          }`}>
            {t("su_save_20")}
          </span>
        </div>
      </div>

      {/* Cards — Pro is anchor (larger, lime border) */}
      <div className="grid md:grid-cols-3 gap-4 md:gap-3 md:items-stretch">
        {PLANS_UI.map((p) => {
          const active = data.plan === p.key;
          const isRec = p.recommended;
          const price = displayPrice(p);
          const Icon = p.icon;
          return (
            <div
              key={p.key}
              data-testid={`plan-card-${p.key}`}
              onClick={() => set({ plan: p.key })}
              className={`relative flex flex-col rounded-[24px] border-2 transition-all cursor-pointer ${
                isRec
                  ? "p-7 md:-my-3 border-[#CCF22C] bg-white shadow-[0_10px_30px_-8px_rgba(25,53,41,0.15)] z-10"
                  : "p-6"
              } ${
                !isRec && (active ? "border-[#193529] bg-[#F5F2E8]/50" : "border-[#193529]/15 bg-white/70 hover:border-[#193529]/40")
              }`}
            >
              {isRec && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#CCF22C] text-[#193529] text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full inline-flex items-center gap-1 shadow-sm">
                  <Sparkle size={10} weight="fill" /> {t("su_most_chosen")}
                </div>
              )}

              {/* Icon + name */}
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${
                isRec ? "bg-[#CCF22C]" : "bg-[#F5F2E8]"
              }`}>
                <Icon size={24} weight="fill" style={{ color: p.iconColor }} />
              </div>

              <div className="font-display font-black text-2xl text-[#193529] leading-tight">{p.name}</div>
              <div className="text-xs text-[#5A6E63] mt-1 min-h-[32px]">{p.tagline}</div>

              {/* Price block */}
              <div className="mt-5 pb-5 border-b border-[#193529]/10">
                <div className="flex items-baseline gap-1">
                  <span className="font-display font-black text-4xl text-[#193529] tf-numeric leading-none">
                    € {price.toString().replace(".", ",")}
                  </span>
                  <span className="text-xs text-[#5A6E63] pb-1">{t("su_per_month")}</span>
                </div>
                {isYearly ? (
                  <div className="text-[11px] text-[#4A8A3E] font-bold mt-1.5 tf-numeric">
                    {t("su_billed_yearly")} {fmtEur(p.priceYearlyPerMonth * 12)}/{t("su_yearly").toLowerCase()}
                  </div>
                ) : (
                  <div className="text-[11px] text-[#5A6E63] mt-1.5">{t("su_renew_monthly")}</div>
                )}
              </div>

              {/* Feature list */}
              <ul className="mt-5 space-y-2 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    {f.pre ? (
                      <>
                        <ArrowDown size={12} className="text-[#5A6E63] shrink-0 mt-1" />
                        <span className="text-[#5A6E63] font-bold italic">{f.text}</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle
                          size={16} weight="fill"
                          className={`shrink-0 mt-0.5 ${f.highlight ? "text-[#4A8A3E]" : "text-[#4A8A3E]"}`}
                        />
                        <span className={`text-[#193529] ${f.bold ? "font-black" : ""} ${f.highlight ? "" : ""}`}>
                          {f.text}
                          {f.highlight && <span className="ml-1 inline-block bg-[#CCF22C]/40 text-[#193529] px-1.5 py-0.5 rounded text-[10px] font-black align-middle">EXCLUSIVE</span>}
                        </span>
                      </>
                    )}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); selectPlanAndContinue(p.key); }}
                data-testid={`plan-cta-${p.key}`}
                className={`mt-6 w-full ${isRec ? "tf-pill-primary" : "tf-pill-secondary"}`}
                disabled={busy}
              >
                {p.cta} <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Back button — no primary "Continua" (each card has its own CTA) */}
      <div className="mt-10 flex items-center justify-between">
        <button type="button" onClick={onBack} className="text-sm text-[#5A6E63] hover:text-[#193529] inline-flex items-center gap-1.5" data-testid="back-btn">
          <ArrowLeft size={14} weight="bold" /> {t("su_back")}
        </button>
        <div className="text-xs text-[#5A6E63]">
          {t("su_questions")} <a href="mailto:info@tee.farm" className="underline font-bold text-[#193529]">info@tee.farm</a>
        </div>
      </div>
    </section>
  );
}

function ArrowDown({ size = 12, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ---------------- Step 6 · Checkout ---------------- */
function StepCheckout({ data, token, onBack, onSuccess }) {
  const { t } = useI18n();
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [amount, setAmount] = useState(0);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setStripePromise(getStripe());
    api.post(`/signup/${token}/checkout`).then(({ data: r }) => {
      setClientSecret(r.client_secret);
      setAmount(r.amount);
    }).catch((e) => setErr(e?.response?.data?.detail || t("error")));
  }, [token, t]);

  if (err) {
    return (
      <section>
        <ScreenHeader eyebrow="06" title={t("su_checkout_error_title")} />
        <div className="rounded-lg border border-[#C4452E]/30 bg-[#C4452E]/5 p-4 text-sm text-[#C4452E] flex items-start gap-2">
          <XCircle size={18} weight="fill" /> {err}
        </div>
        <ActionBar onBack={onBack} onNext={() => window.location.reload()} cta={t("su_retry")} />
      </section>
    );
  }
  if (!clientSecret) {
    return <section><ScreenHeader eyebrow="06" title={t("su_checkout_preparing")} subtitle={t("su_checkout_preparing_sub")} /></section>;
  }
  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: appearance() }}>
      <CheckoutForm data={data} amount={amount} onBack={onBack} onSuccess={onSuccess} token={token} />
    </Elements>
  );
}

function CheckoutForm({ data, amount, token, onBack, onSuccess }) {
  const { t } = useI18n();
  const stripe = useStripe();
  const elements = useElements();
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const PLANS_UI = usePlansUi();
  const isYearly = data.plan.period === "yearly";
  const planName = PLANS_UI.find((p) => p.key === data.plan.plan)?.name || "Pro";

  const submit = async (e) => {
    e.preventDefault();
    if (!terms) return setErr(t("su_terms_required"));
    if (!stripe || !elements) return;
    setSubmitting(true); setErr(null);
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.origin + "/signup" },
      redirect: "if_required",
    });
    if (error) { setErr(error.message); setSubmitting(false); return; }
    if (setupIntent?.status === "succeeded") {
      try {
        const { data: res } = await api.post(`/signup/${token}/confirm`, {
          setup_intent_id: setupIntent.id, terms_accepted: true,
        });
        onSuccess(res);
      } catch (e) {
        setErr(e?.response?.data?.detail || t("error"));
        setSubmitting(false);
      }
    }
  };

  return (
    <form onSubmit={submit}>
      <ScreenHeader
        eyebrow="06"
        title={t("su_s6_title")}
        subtitle={t("su_s6_subtitle")}
      />

      <div className="rounded-[20px] bg-[#F5F2E8]/60 border border-[#193529]/10 p-5 mb-8">
        <div className="text-[10px] uppercase tracking-widest text-[#5A6E63]">{t("su_summary")}</div>
        <div className="mt-3 flex items-baseline justify-between">
          <div>
            <div className="font-display font-black text-xl text-[#193529]">Teefarm {planName}</div>
            <div className="text-xs text-[#5A6E63] mt-0.5">
              {isYearly ? t("su_yearly_note") : t("su_monthly_note")}
            </div>
          </div>
          <div className="text-right">
            <div className="font-display font-black text-3xl text-[#193529] tf-numeric">{fmtEur(amount)}</div>
          </div>
        </div>
      </div>

      <div className="rounded-[20px] bg-[#F5F2E8]/60 border border-[#193529]/10 p-5 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-[#5A6E63] flex items-center gap-2 mb-4">
          <Lock size={12} weight="bold" /> {t("su_card_data")}
        </div>
        <PaymentElement options={{ layout: "tabs" }} />
      </div>

      <label className="flex items-start gap-3 p-4 rounded-[20px] bg-[#F5F2E8]/60 border border-[#193529]/10 cursor-pointer mb-6">
        <input
          type="checkbox"
          checked={terms}
          onChange={(e) => setTerms(e.target.checked)}
          className="mt-1 w-4 h-4 accent-[#193529]"
          data-testid="terms-checkbox"
        />
        <div className="text-xs leading-relaxed text-[#193529]">
          {t("su_terms_accept")}{" "}
          <a href="/legal/terms" target="_blank" className="underline font-bold">{t("su_terms_link")}</a>{" "}
          {t("su_terms_clause")}{" "}
          <a href="/legal/privacy" target="_blank" className="underline font-bold">{t("su_privacy_link")}</a>.
          {" "}{t("su_terms_authorize")}
        </div>
      </label>

      {err && (
        <div className="rounded-lg border border-[#C4452E]/30 bg-[#C4452E]/5 p-3 text-sm text-[#C4452E] flex gap-2 mb-6">
          <XCircle size={16} weight="fill" /> {err}
        </div>
      )}

      <div className="flex items-center justify-between mt-4">
        <button type="button" onClick={onBack} className="text-sm text-[#5A6E63] hover:text-[#193529] inline-flex items-center gap-1.5" data-testid="back-btn">
          <ArrowLeft size={14} weight="bold" /> {t("su_back")}
        </button>
        <button type="submit" disabled={!stripe || submitting || !terms} className="tf-pill-primary" data-testid="checkout-submit">
          {submitting ? t("su_processing") : <><Lock size={14} weight="bold" /> {t("su_pay")} {fmtEur(amount)}</>}
        </button>
      </div>

      <div className="mt-6 flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest text-[#5A6E63]">
        <ShieldCheck size={12} weight="duotone" /> {t("su_secure_payment")}
      </div>
    </form>
  );
}

function appearance() {
  return {
    theme: "stripe",
    variables: {
      colorPrimary: "#193529",
      colorText: "#193529",
      fontFamily: "Nohemi, system-ui, sans-serif",
      borderRadius: "8px",
      colorBackground: "#ffffff",
    },
  };
}

export function SignupSuccess() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const location = useLocation();
  const ref = params.get("ref");
  const [status, setStatus] = useState(location.state || null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (ref && !status) {
      api.get(`/signup/status/${ref}`)
        .then(({ data }) => setStatus(data))
        .catch(() => setNotFound(true));
    }
  }, [ref, status]);

  const isActivated = status?.status === "active";

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 relative" style={{ background: "#F5F2E8" }}>
        <div className="absolute top-6 right-6"><LanguageSwitcher /></div>
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-[#FBDDE1] flex items-center justify-center mx-auto">
            <XCircle size={40} weight="fill" className="text-[#A11534]" />
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl mt-6 text-[#193529]">{t("su_invalid_ref_title")}</h1>
          <p className="text-sm text-[#5A6E63] mt-3">{t("su_invalid_ref_desc")}</p>
          <a href="/" className="tf-btn tf-btn-primary mt-6 inline-flex">{t("su_back_home")}</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#F5F2E8" }}>
      <div className="flex justify-end px-6 pt-6">
        <LanguageSwitcher />
      </div>
      <div className="max-w-2xl mx-auto px-6 py-12 md:py-16 text-center" data-testid="signup-success">
        {isActivated ? (
          <div className="w-24 h-24 mx-auto rounded-full bg-[#CCF22C] flex items-center justify-center">
            <CheckCircle size={48} weight="fill" className="text-[#193529]" />
          </div>
        ) : (
          <HourglassAnim />
        )}
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#5A6E63] mt-8 flex items-center justify-center gap-2">
          <Sparkle size={12} weight="fill" /> {isActivated ? t("su_success_activated") : t("su_success_pending")}
        </div>
        <h1 className="font-display font-black text-4xl md:text-5xl mt-4 tracking-tight text-[#193529]">
          {isActivated ? t("su_success_ready") : t("su_success_almost")}
        </h1>
        <p className="text-lg text-[#5A6E63] mt-4 max-w-lg mx-auto">
          {isActivated
            ? t("su_success_active_body", { company: status?.company_name || "" })
            : t("su_success_pending_body")}
        </p>

        {status?.company_name && !isActivated && (
          <div className="mt-6 inline-block px-4 py-2 rounded-full bg-white border border-[#E0DFD4] text-xs text-[#193529]">
            {t("su_success_queue", { company: status.company_name, plan: (status.subscription_plan || "").toUpperCase() })}
          </div>
        )}

        <div className="bg-white border border-[#E0DFD4] rounded-[24px] p-8 md:p-10 mt-10 text-left space-y-4">
          <div className="text-[10px] uppercase tracking-widest text-[#5A6E63]">{t("su_success_whatnow")}</div>
          <Row n="1" title={t("su_success_row1_title")} body={t("su_success_row1_body")} done={isActivated} />
          <Row n="2" title={t("su_success_row2_title")} body={t("su_success_row2_body")} done={isActivated} />
          <Row n="3" title={t("su_success_row3_title")} body={t("su_success_row3_body")} done={isActivated} />
        </div>

        {isActivated && (
          <a href="/login" className="tf-pill-primary mt-8" data-testid="goto-login-btn">
            {t("su_goto_login")} <ArrowRight size={14} weight="bold" />
          </a>
        )}

        {/* Task 4: sempre disponibile "Torna alla Home" per uscire dal wizard */}
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs text-[#5A6E63] hover:text-[#193529] underline underline-offset-4 transition-colors"
            data-testid="back-home-btn"
          >
            <House size={14} weight="bold" /> {t("su_back_home")}
          </Link>
        </div>

        <div className="text-xs text-[#5A6E63] mt-8">
          {t("su_questions_footer")} <a href="mailto:info@tee.farm" className="underline">info@tee.farm</a>
          {ref && <div className="mt-2 opacity-50">{t("su_request_id")} <span className="tf-numeric">{ref.slice(0, 8)}…</span></div>}
        </div>
      </div>
    </div>
  );
}

function HourglassAnim() {
  return (
    <div className="relative w-24 h-24 mx-auto" data-testid="hourglass-anim">
      <div className="absolute inset-0 rounded-full bg-[#CCF22C] animate-pulse-slow" />
      <div className="absolute inset-0 flex items-center justify-center animate-hourglass-rotate">
        <svg width="42" height="42" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#193529]">
          <path d="M6 2h12v3l-4.5 5.5v3L18 19v3H6v-3l4.5-5.5v-3L6 5V2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" fill="currentColor" fillOpacity="0.15" />
          <path d="M8 4h8v1c0 2-2 3.5-4 3.5S8 7 8 5V4z" fill="currentColor" />
          <path d="M9 20h6c0-1.5-1.5-3-3-3s-3 1.5-3 3z" fill="currentColor" />
        </svg>
      </div>
    </div>
  );
}

const Row = ({ n, title, body, done }) => (
  <div className="flex items-start gap-3">
    <span className={`w-6 h-6 rounded-full flex items-center justify-center font-black text-sm tf-numeric shrink-0 ${
      done ? "bg-[#4A8A3E] text-white" : "bg-[#CCF22C] text-[#193529]"
    }`}>
      {done ? <CheckCircle size={14} weight="fill" /> : n}
    </span>
    <div className={`text-sm ${done ? "text-[#5A6E63] line-through" : "text-[#193529]"}`}>
      <b>{title}</b> — {body}
    </div>
  </div>
);
